// api_recordingAngles.ts — Enregistrement multi-angle : paths éphémères + publication WHIP
//
// Un angle est une source du deck enregistrée dans son propre fichier, en parallèle du
// programme. MediaMTX enregistre par path : il faut donc un path par angle, et un flux
// qui y publie. Ce module gère les deux bouts de ce cycle de vie :
//
//   1. createAnglePath()  → admin-api crée `src_{uuid}_{angleId}` et retourne son URL WHIP.
//   2. publishAngle()     → la régie y publie la MediaStream de la source.
//   3. (admin-api démarre l'enregistrement sur tous les paths d'un coup)
//   4. closeAnglePublication() à l'arrêt ; les PATHS, eux, sont détruits côté serveur par
//      /recording/stop — seul le manager sait lesquels appartenaient à la session.
//
// L'ordre 1→2→3 n'est pas négociable : MediaMTX n'écrit aucun fichier sur un path sans
// publisher, et le manager ne peut alors pas résoudre le fichier à renommer au stop.
//
// Coût à garder en tête : chaque angle est un encodeur et un flux montant de plus dans le
// navigateur. Trois sources cochées = quatre flux sortants avec le programme.

import axios from 'axios';

import { API_URLS } from '../urls';
import { forceSdpCodecs, withStartBitrate } from '../../dataTypes/liveStreamManagment/utils_sdp';
import { defaultRTCConfig } from '../../dataTypes/liveStreamManagment/utils_webrtc';
import { trackPeerConnection, listTrackedPeerConnections } from './api_webrtcBuffers';
import { deleteWhipSession } from './api_whip';
import { createAngleCanvasTrack, AngleCanvasSource } from '../../dataTypes/liveStreamManagment/angleCanvas';

// Plafond de débit par angle : le MÊME que le programme depuis le 25/08/2026, sur
// décision d'Alex. Un angle est une archive, il n'a pas à être servi moins bien que
// l'antenne quand le lien a de la marge.
//
// ⚠️ C'est la SOMME qui compte : trois angles plus le programme demandent 32 Mb/s au
// lien montant de la machine de régie. Au-delà de ce que le lien porte, le
// navigateur arbitre — et c'est `networkPriority` qui décide alors qui perd.
export const ANGLE_MAX_BITRATE = 12_000_000;

// Marge d'attente de l'établissement de la connexion WHIP d'un angle. Au-delà, on
// considère l'angle perdu : mieux vaut enregistrer le programme seul que de retarder
// indéfiniment le démarrage de la prise.
const ANGLE_CONNECT_TIMEOUT_MS = 8_000;

// Après l'établissement de la connexion, on exige que des octets partent RÉELLEMENT.
// Une PeerConnection peut atteindre 'connected' sans qu'aucune image ne circule — c'est
// le cas d'une piste issue de captureStream() sur un <video> qui ne rend rien. MediaMTX
// ne crée alors aucun fichier, et l'angle est perdu en silence : c'est exactement ce qui
// est arrivé à la source « Playlist » lors du premier essai du 2026-08-18.
//
// Porté de 4 s à 8 s le 25/08/2026. Sur Nasa TV, le troisième angle d'une prise à trois
// sources était établi (MediaMTX : « peer connection established », par relais TURN) mais
// n'émettait toujours rien 4 s plus tard, quand les deux autres publiaient en 40 ms. Le
// coût de cette marge est payé UNE fois — les angles sont attendus en parallèle — et
// seulement quand une source est réellement muette.
const ANGLE_MEDIA_TIMEOUT_MS  = 8_000;
const ANGLE_MEDIA_POLL_MS     = 250;

// ─── Publication d'un angle distant par canvas — ÉTEINT ───────────────────────
//
// `false` : l'angle d'une caméra distante publie sa piste telle qu'elle arrive.
// C'est le comportement historique, et celui qui échoue parfois sans créer
// d'encodeur (cf. `angleCanvas.ts` pour le diagnostic complet).
//
// Le canvas répare ce cas — il donne au sender une piste LOCALE, la seule forme
// d'entrée qui n'ait jamais échoué — mais il coûte un <video>, un canvas et une
// boucle de dessin PAR angle distant. Sur une prise à trois téléphones, c'est
// trois compositions en plus du programme. Trop cher pour être le régime normal.
//
// La vraie réponse est le transport SFU : le téléphone publie lui-même son path
// MediaMTX, l'angle est alors enregistré à la source et il n'y a plus rien à
// republier — ni piste distante, ni canvas. Le canvas reste ici en DERNIER
// RECOURS, pour les appareils retombés en P2P où le chemin direct échouerait.
//
// Le basculer à `true` suffit à l'activer : rien d'autre à toucher.
const ANGLE_CANVAS_ENABLED = false;

// Le rassemblement ICE plafonnait à 5 s par angle, systématiquement atteint : trois
// angles retardaient le démarrage de l'enregistrement de plus de quinze secondes, autant
// de programme non enregistré. Les candidats hôtes sont connus bien avant.
const ANGLE_ICE_TIMEOUT_MS    = 2_000;

// ─── Santé du flux ENTRANT d'une caméra distante ──────────────────────────────
//
// `waitForOutboundMedia` (plus bas) surveille le mauvais bout : il attend des images
// SORTANTES sur l'angle, et ne peut donc conclure qu'après 8 s, en ayant déjà créé un
// path côté serveur — path qu'il faut ensuite détruire. Pire, son message accuse la
// publication (« aucune image émise ») alors que la cause est en amont.
//
// Le 25/08/2026 sur Nasa TV, le téléphone « alex » a perdu son angle trois fois de
// suite, avec `limitation=none, images=0, octets=0`, ce qui avait été lu comme « la
// source est muette ». Cette lecture-là s'est révélée FAUSSE le 27/08 (voir
// `waitForOutboundMedia`) : `limitation=none` ne désigne pas la source.
//
// La sonde reste néanmoins utile, pour ce qu'elle mesure vraiment : `framesDecoded`
// progresse-t-il sur la connexion « montant » de cet appareil, AVANT qu'on crée le
// path ? Elle attrape le vrai cas d'un appareil en cours de reconnexion, coûte 600 ms
// au lieu de 8 s, et évite de créer un path pour rien. Elle ne dit rien du cas où la
// source débite et l'angle n'émet pas — c'est `waitForOutboundMedia` qui le documente.
//
// `waitForOutboundMedia` reste en place : il n'est pas faux, il est tardif. Il attrape
// les cas que celui-ci ne voit pas — une source non-WebRTC, comme le `captureStream()`
// d'un `<video>` qui ne rend rien (la Playlist du 2026-08-18).
const INBOUND_PROBE_MS = 600;

// Un appareil peut arriver par deux chemins : sa PeerConnection directe (« montant »)
// ou, en mode SFU, une lecture WHEP du path MediaMTX. Les deux comptent — n'en regarder
// qu'un ferait déclarer muet un appareil qui publie parfaitement par l'autre.
const KINDS_ENTRANTS = ['montant', 'whep'];

/**
 * Images décodées sur les connexions entrantes de cet appareil.
 *
 * Trois réponses possibles, et la distinction compte :
 *   - `{ images }`   : on sait compter, la comparaison peut se faire.
 *   - `{ probleme }` : une connexion existe mais ne porte aucune vidéo — c'est un fait.
 *   - `null`         : AUCUNE connexion connue. On ne conclut rien. Un transport que ce
 *                      module ne connaît pas ne doit jamais faire condamner un angle qui
 *                      fonctionne ; `waitForOutboundMedia` reste là pour ces cas.
 */
const compterImagesEntrantes = async (
  deviceId: string,
): Promise<{ images: number } | { probleme: string } | null> => {
  const connexions = listTrackedPeerConnections()
    .filter(t => KINDS_ENTRANTS.includes(t.kind) && t.label === deviceId)
    .filter(t => t.pc.connectionState !== 'closed');

  if (connexions.length === 0) return null;

  let images = 0;
  let trouvee = false;
  for (const { pc } of connexions) {
    const stats = await pc.getStats();
    stats.forEach(report => {
      if (report.type === 'inbound-rtp' && report.kind === 'video') {
        images += report.framesDecoded || 0;
        trouvee = true;
      }
    });
  }

  return trouvee ? { images } : { probleme: 'aucune piste vidéo entrante' };
};

/**
 * Relevé DÉTAILLÉ des connexions entrantes d'un appareil. Sert uniquement à documenter
 * l'échec d'un angle : `compterImagesEntrantes` ne compte que les images décodées, or ce
 * qu'on veut savoir quand un angle n'émet rien, c'est si la SOURCE débitait pendant ce
 * temps-là — et en quelle définition, par quel décodeur.
 *
 * `null` quand aucune connexion entrante n'est connue (source non-WebRTC : webcam
 * locale, capture d'un `<video>`, entrée OBS). On ne conclut alors rien.
 */
interface ReleveEntrant {
  recues   : number;
  decodees : number;
  largeur  : number;
  hauteur  : number;
  decodeur : string;
}

const releverEntrant = async (deviceId: string): Promise<ReleveEntrant | null> => {
  const connexions = listTrackedPeerConnections()
    .filter(t => KINDS_ENTRANTS.includes(t.kind) && t.label === deviceId)
    .filter(t => t.pc.connectionState !== 'closed');

  if (connexions.length === 0) return null;

  const releve: ReleveEntrant = {
    recues: 0, decodees: 0, largeur: 0, hauteur: 0, decodeur: '—',
  };
  let trouvee = false;

  for (const { pc } of connexions) {
    const stats = await pc.getStats();
    stats.forEach(report => {
      if (report.type === 'inbound-rtp' && report.kind === 'video') {
        trouvee = true;
        releve.recues   += report.framesReceived || 0;
        releve.decodees += report.framesDecoded  || 0;
        // La définition et le décodeur ne s'additionnent pas : on garde ceux de la
        // dernière piste vue, un appareil n'en ayant qu'une en pratique.
        if (report.frameWidth)  releve.largeur  = report.frameWidth;
        if (report.frameHeight) releve.hauteur  = report.frameHeight;
        if (report.decoderImplementation) releve.decodeur = report.decoderImplementation;
      }
    });
  }

  return trouvee ? releve : null;
};

/**
 * Ce que la source a livré PENDANT l'attente, en une phrase. C'est la mesure qui
 * départage les deux causes possibles d'un angle sans image :
 *
 *   - la source a décodé des images et le sender n'en a encodé aucune → le blocage est
 *     dans le rattachement de la piste distante au nouvel encodeur ;
 *   - la source n'a rien décodé non plus → le flux montant de l'appareil s'est tu.
 */
const decrireEntrant = (avant: ReleveEntrant | null, apres: ReleveEntrant | null): string => {
  if (!avant || !apres) return 'flux entrant : non mesurable (source non-WebRTC ou connexion fermée)';
  const definition = apres.largeur && apres.hauteur ? `${apres.largeur}×${apres.hauteur}` : '—';
  return `flux entrant pendant l'attente : +${apres.decodees - avant.decodees} image(s) décodée(s), `
    + `+${apres.recues - avant.recues} reçue(s), ${definition}, décodeur ${apres.decodeur}`;
};

/**
 * Deux relevés espacés : le flux entrant de cet appareil livre-t-il des images ?
 * Renvoie `null` si tout va bien, sinon la raison, prête à être affichée.
 *
 * À utiliser AVANT de créer le path d'un angle — un appareil muet ici le restera à la
 * publication, et l'échec coûtera alors 8 s et un path créé pour rien.
 */
export const probeInboundVideo = async (deviceId: string): Promise<string | null> => {
  const avant = await compterImagesEntrantes(deviceId);
  if (avant === null)           return null;
  if ('probleme' in avant)      return avant.probleme;

  await new Promise(resolve => setTimeout(resolve, INBOUND_PROBE_MS));

  const apres = await compterImagesEntrantes(deviceId);
  if (apres === null)           return null;
  if ('probleme' in apres)      return apres.probleme;

  return apres.images > avant.images
    ? null
    : `aucune image reçue en ${INBOUND_PROBE_MS / 1000} s`;
};

// Dernier relevé par appareil, pour `inspectInboundVideo` : celui-ci est appelé en
// boucle par la fenêtre d'enregistrement, il compare donc deux appels successifs
// plutôt que de s'imposer une attente à chaque fois.
const dernierReleveEntrant = new Map<string, { images: number; a: number }>();

/**
 * Même question que `probeInboundVideo`, mais SANS attente : la comparaison se fait
 * avec l'appel précédent. Destinée à un sondage périodique (la fenêtre
 * « Enregistrement d'une session », qui réévalue ses sources tant qu'elle est ouverte).
 *
 * Le tout premier appel ne peut rien conclure et répond `null` — le suivant tranchera.
 */
export const inspectInboundVideo = async (deviceId: string): Promise<string | null> => {
  const releve = await compterImagesEntrantes(deviceId);
  if (releve === null || 'probleme' in releve) {
    // Le compteur repart de zéro : au retour de l'appareil, la première comparaison ne
    // doit pas se faire contre un relevé d'avant la coupure.
    dernierReleveEntrant.delete(deviceId);
    return releve === null ? null : releve.probleme;
  }

  const { images } = releve;
  const precedent  = dernierReleveEntrant.get(deviceId);
  dernierReleveEntrant.set(deviceId, { images, a: Date.now() });

  if (!precedent)                return null;
  if (images > precedent.images) return null;

  return `aucune image reçue depuis ${Math.max(1, Math.round((Date.now() - precedent.a) / 1000))} s`;
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AnglePathInfo {
  angleId : string;
  path    : string;
  whipUrl : string;
}

export interface AnglePublication {
  angleId : string;
  whipUrl : string;
  pc      : RTCPeerConnection;
  /**
   * Canvas intercalé pour une caméra distante, à libérer avec la publication —
   * sans quoi une boucle de dessin et un <video> caché survivent à chaque prise.
   * Absent pour une source locale, qui publie sa piste en direct.
   */
  canvas? : AngleCanvasSource | null;
}

// ─── Paths (admin-api) ────────────────────────────────────────────────────────

const angleUrl = (channelUuid: string, angleId: string): string =>
  API_URLS.channelRecordingAngle
    .replace('{channelUuid}', channelUuid)
    .replace('{angleId}', encodeURIComponent(angleId));

export const createRecordingAnglePath = async (
  channelUuid: string,
  angleId: string,
): Promise<AnglePathInfo> => {
  const response = await axios.post(angleUrl(channelUuid, angleId), new FormData());

  if (response.data?.code !== 1 || !response.data?.whipUrl) {
    throw new Error(response.data?.comment ?? `Erreur création du path d'angle ${angleId}`);
  }

  return {
    angleId,
    path:    response.data.path ?? '',
    whipUrl: response.data.whipUrl,
  };
};

/**
 * Rattrapage : détruit un path d'angle resté en place parce que sa publication a échoué
 * avant que l'enregistrement n'ait démarré. Le cas normal passe par /recording/stop.
 * Silencieux — un path orphelin est un défaut d'hygiène, jamais une raison de faire
 * échouer l'arrêt d'un enregistrement.
 */
export const deleteRecordingAnglePath = async (
  channelUuid: string,
  angleId: string,
): Promise<void> => {
  try {
    await axios.delete(angleUrl(channelUuid, angleId));
  } catch (err) {
    console.warn(`[angles] Destruction du path ${angleId} échouée`, err);
  }
};

// ─── Publication WHIP ─────────────────────────────────────────────────────────

/**
 * Publie une MediaStream sur l'URL WHIP d'un angle et attend que la connexion soit
 * établie. Volontairement plus simple que startWhipStreaming() du programme : pas de
 * canvas de composition, pas de mixer audio, pas de monitoring de stats, et surtout PAS
 * de reconnexion automatique — un angle qui tombe en cours de prise laisse son fichier
 * tronqué là où il s'est arrêté, ce qui vaut mieux qu'une reconnexion qui ferait repartir
 * MediaMTX sur un second fichier que le renommage au stop ne saurait plus désigner.
 */
/**
 * La piste audio qu'un angle publie. Une piste IMPOSÉE (et vivante) l'emporte sur tout : c'est la sortie du
 * programme, demandée par le Switch audio « Fixe ». Sinon l'audio de la source, à défaut le micro qui lui est
 * associé. Isolée pour être testée : le reste de publishAngle exige une vraie PeerConnection.
 */
export const chooseAngleAudioTrack = (
  own    : MediaStreamTrack | undefined,
  secours: MediaStreamTrack | null | undefined,
  forced?: MediaStreamTrack | null,
): MediaStreamTrack | undefined => {
  if (forced && forced.readyState === 'live') return forced;
  return own ?? secours ?? undefined;
};

export const publishAngle = async (
  angleId: string,
  whipUrl: string,
  stream: MediaStream,
  /**
   * Piste audio à joindre quand la source vidéo n'en porte aucune.
   *
   * C'est le cas de TOUTE caméra locale : `DeviceAcquirer` ouvre `getUserMedia`
   * avec `audio: false`, le micro étant géré à part par le mixer. L'angle d'une
   * webcam sortait donc muet (MediaMTX : « 1 track (H264) ») alors que le
   * programme, lui, portait le son — il prend le bus du mixer, pas la source.
   *
   * On joint ici le micro que l'opérateur a associé à CETTE caméra, pas le
   * programme mixé : un angle doit restituer sa propre source.
   */
  audioDeSecours?: MediaStreamTrack | null,
  /**
   * Appareil d'où vient la piste, quand la source est une caméra distante
   * (`remote-<id>` → `<id>`). Il commande DEUX choses :
   *
   *   - la publication passe par un canvas plutôt que par la piste distante brute
   *     — mais SEULEMENT si `ANGLE_CANVAS_ENABLED` est vrai, ce qu'il n'est pas
   *     par défaut (cf. la constante plus haut : trop cher pour le régime
   *     normal, gardé en dernier recours) ;
   *   - le relevé du flux ENTRANT au moment d'un échec, sans quoi le message ne
   *     peut pas dire si la source débitait.
   *
   * Absent pour une source locale : sa piste part en direct, comme avant.
   */
  sourceDeviceId?: string | null,
  /**
   * Piste audio IMPOSÉE à la place de celle de la source — la sortie du programme quand le Switch audio est
   * « Fixe » (cf. angleAudio.ts). Absente pour l'enregistrement multi-angle, où un angle restitue sa source.
   */
  audioImposee?: MediaStreamTrack | null,
): Promise<AnglePublication> => {
  const pc = new RTCPeerConnection(defaultRTCConfig());
  trackPeerConnection(pc, 'angle', angleId);

  let canvas: AngleCanvasSource | null = null;

  try {
    const pisteSource = stream.getVideoTracks()[0];
    if (!pisteSource) throw new Error('aucune piste vidéo sur la source');

    // Une piste terminée ou en sourdine ne produira jamais d'image. `muted` ici est celui
    // de MediaStreamTrack — « aucune donnée ne circule en ce moment », sans rapport avec
    // le volume — et c'est le symptôme d'un <video> capturé qui ne rend pas.
    if (pisteSource.readyState !== 'live') {
      throw new Error(`piste vidéo ${pisteSource.readyState} (attendu : live)`);
    }
    if (pisteSource.muted) {
      throw new Error("piste vidéo en sourdine (aucune image ne circule) — source à l'arrêt ou non rendue");
    }

    // Caméra distante : on republie un CANVAS, pas la piste reçue. C'est la seule
    // différence de structure qui subsistait entre le programme (qui n'a jamais
    // échoué) et l'angle d'un téléphone (qui échoue sans que l'encodeur naisse).
    // Une source locale garde le chemin direct : il fonctionne, et lui imposer un
    // canvas serait un coût sans contrepartie.
    if (ANGLE_CANVAS_ENABLED && sourceDeviceId) {
      canvas = await createAngleCanvasTrack(stream);
    }

    const videoTrack = canvas?.track ?? pisteSource;

    try {
      // Même intention que le programme : privilégier la fluidité au piqué sur une
      // source de diffusion.
      videoTrack.contentHint = 'motion';
    } catch { /* propriété non supportée : sans effet */ }

    const sender = pc.addTrack(videoTrack, new MediaStream([videoTrack]));

    try {
      const params = sender.getParameters();
      if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
      params.encodings[0].maxBitrate = ANGLE_MAX_BITRATE;
      // Sous le programme, qui est à l'antenne : les angles sont enregistrés, pas diffusés.
      // Chaque publication a sa propre PeerConnection, donc son propre contrôle de
      // congestion, aveugle aux autres — cette priorité est le seul levier qui dise au
      // navigateur quoi sacrifier en premier quand le lien montant sature.
      // Le débit visé est le même que celui du programme, la PRIORITÉ non : lui est
      // à l'antenne. Elle ne joue que lorsque le lien sature, et là, un fichier
      // d'archive doit céder avant l'émission en cours. C'est le seul écart que je
      // maintiens avec « tout le monde pareil » — dis-le si tu veux l'aligner aussi.
      params.encodings[0].networkPriority = 'low';
      // ── Définition figée, comme le programme ──────────────────────────────
      //
      // `balanced`, posé le matin du 25/08/2026 pour débloquer un angle qui ne
      // démarrait jamais, a montré son revers l'après-midi : l'angle de la webcam
      // est sorti en **640×360 pour 6,6 Mb/s**, mesuré à l'ffprobe sur le fichier.
      // La place ne manquait pas — l'encodeur avait réduit l'image parce qu'on
      // l'y autorisait.
      //
      // Un enregistrement n'est pas une diffusion : il se regarde après, souvent
      // sur grand écran, et une définition perdue ne se rattrape jamais. Une image
      // par seconde de moins, si. D'où `maintain-resolution` — l'encodeur sacrifie
      // la CADENCE et garde la définition — et `scaleResolutionDownBy = 1`, qui lui
      // interdit de réduire l'image à la source. C'est le réglage du programme,
      // pour la même raison.
      //
      // ⚠️ Le risque assumé : sous une machine saturée, la cadence peut tomber très
      // bas, voire à zéro — c'est le blocage du matin. Il est désormais visible
      // (colonne Définition, motif `cpu` dans les journaux) et le délai d'attente
      // des premières images est passé de 4 à 8 s. Si un angle échoue encore avec
      // `cpu`, la réponse n'est plus dans ce réglage mais dans le NOMBRE d'angles
      // 1080p qu'une seule machine peut encoder.
      params.degradationPreference = 'maintain-resolution';
      params.encodings[0].scaleResolutionDownBy = 1;
      await sender.setParameters(params);
    } catch { /* setParameters non supporté : on publie sans plafond explicite */ }

    // L'audio de la source si elle en porte, sinon le micro qui lui est associé : un angle
    // restitue sa propre source, pas le programme mixé. Seule exception, `audioImposee` :
    // le Switch audio « Fixe » demande le programme sur tous les angles de la multivue.
    const audioTrack = chooseAngleAudioTrack(stream.getAudioTracks()[0], audioDeSecours, audioImposee);
    if (audioTrack && audioTrack.readyState === 'live') {
      pc.addTrack(audioTrack, new MediaStream([audioTrack]));
    } else {
      console.warn(`[REC] 🔇 Angle ${angleId} publié SANS audio — ni piste sur la source, ni micro associé`);
    }

    const offer = await pc.createOffer();
    if (!offer.sdp) throw new Error('SDP vide');

    // MediaMTX n'accepte qu'un profil H264 : même filtrage que le programme, sans quoi
    // l'Answer peut désigner un profil que la chaîne aval ne sait pas relire.
    // Le débit de départ compte doublement sur un enregistrement : les secondes
    // molles du démarrage restent dans le fichier. On demande donc à l'encodeur de
    // commencer haut plutôt que de sonder le lien pendant dix secondes — la marge,
    // elle, a été mesurée et elle est large.
    //
    // ⚠️ PLUS DE PLANCHER depuis le 27/08/2026. `x-google-min-bitrate=2000` était posé
    // ici pour empêcher l'encodeur de redescendre au premier hoquet. Mais un plancher
    // est une EXIGENCE : sur un lien qui ne peut pas la tenir, elle est le suspect
    // principal du symptôme mesuré ce jour-là — `encodeur=absent`, zéro image, zéro
    // octet, alors que la source décodait 30 images par seconde en 1080p sans une
    // perte. Cinq échecs, tous avec un montant estimé sous 2 Mb/s ; la seule réussite,
    // avec 9,6 Mb/s. Un angle a le droit de commencer petit — un fichier moche vaut
    // mieux qu'un fichier absent.
    //
    // Changement d'UNE seule variable, délibérément : `maintain-resolution` et
    // `scaleResolutionDownBy = 1` restent en place, sans quoi une prise réussie ne
    // dirait pas lequel des trois réglages était en cause.
    const sdpAngle = withStartBitrate(
      forceSdpCodecs(offer.sdp),
      Math.round(ANGLE_MAX_BITRATE * 0.75 / 1000),
    );
    await pc.setLocalDescription({ type: 'offer', sdp: sdpAngle });

    await new Promise<void>(resolve => {
      if (pc.iceGatheringState === 'complete') { resolve(); return; }
      const check = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', check);
          resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', check);
      setTimeout(resolve, ANGLE_ICE_TIMEOUT_MS);
    });

    const res = await fetch(whipUrl, {
      method:  'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body:    pc.localDescription!.sdp,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`WHIP ${res.status}${body ? ': ' + body : ''}`);
    }

    await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });

    // On attend la connexion effective : démarrer l'enregistrement avant que le flux
    // n'arrive ferait rater au manager la fenêtre de résolution du fichier.
    await waitForConnection(pc);

    // Puis que des images partent vraiment. « connected » ne le garantit pas, et un angle
    // qui ne publie rien fait démarrer un enregistrement sur un path qui restera vide.
    await waitForOutboundMedia(pc, videoTrack, canvas !== null, sourceDeviceId);

    return { angleId, whipUrl, pc, canvas };
  } catch (err) {
    canvas?.dispose();
    pc.close();
    await deleteWhipSession(whipUrl);
    throw err;
  }
};

const waitForConnection = (pc: RTCPeerConnection): Promise<void> =>
  new Promise((resolve, reject) => {
    if (pc.connectionState === 'connected') { resolve(); return; }

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`connexion WHIP non établie après ${ANGLE_CONNECT_TIMEOUT_MS / 1000}s`));
    }, ANGLE_CONNECT_TIMEOUT_MS);

    const cleanup = () => {
      clearTimeout(timer);
      pc.removeEventListener('connectionstatechange', onChange);
    };

    const onChange = () => {
      if (pc.connectionState === 'connected') { cleanup(); resolve(); }
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        cleanup();
        reject(new Error(`connexion WHIP ${pc.connectionState}`));
      }
    };

    pc.addEventListener('connectionstatechange', onChange);
  });

/**
 * Attend que des images partent réellement sur la PeerConnection : `framesEncoded` progresse
 * et `bytesSent` dépasse zéro. Sans cette vérification, un angle dont la source ne produit
 * aucune image démarre un enregistrement sur un path qui restera vide, et l'échec n'est
 * visible qu'après coup, dans les logs du serveur.
 *
 * ── Ce que le message d'échec doit permettre de conclure ──────────────────────
 *
 * ⚠️ `qualityLimitationReason` ne tranche PAS à lui seul, contrairement à ce qui était
 * écrit ici jusqu'au 27/08/2026. Il vaut `none` tant qu'aucun encodage n'a commencé :
 * avec `images=0`, la valeur ne dit rien du tout, et l'avoir lue comme « le silence
 * vient de la source » a envoyé le diagnostic du 25/08 dans le mur. Elle ne redevient
 * informative (`bandwidth`, `cpu`) qu'une fois l'encodeur en marche.
 *
 * Les trois mesures qui, elles, concluent :
 *
 *   - `encoderImplementation` ABSENT ⇒ l'encodeur n'a jamais été initialisé. C'est le
 *     partage décisif entre « l'encodeur tourne et n'a rien à encoder » et « l'encodeur
 *     n'a jamais démarré ».
 *   - la définition vue par le sender (`frameWidth×frameHeight`) : nulle si aucune trame
 *     n'a atteint l'encodeur.
 *   - ce que la SOURCE a décodé pendant les mêmes 8 s (cf. `decrireEntrant`). Des images
 *     décodées en amont pendant que le sender reste à zéro désignent le rattachement de
 *     la piste distante au nouvel encodeur, et rien d'autre.
 *
 * Le 27/08/2026, quatre angles d'iPhone ont échoué avec la connexion établie et zéro
 * octet, alors qu'un téléphone Android relayé par le même chemin passait dans la même
 * seconde. Voir `angles-iphone-2026-08-27.md`.
 */
const waitForOutboundMedia = async (
  pc: RTCPeerConnection,
  track: MediaStreamTrack,
  /** Chemin réellement emprunté — le drapeau `ANGLE_CANVAS_ENABLED` décide, pas la source. */
  viaCanvas: boolean,
  /**
   * Appareil d'où vient la piste, quand c'est une caméra distante. Sert à relever en
   * parallèle ce que le flux ENTRANT a livré pendant l'attente — la seule façon de
   * savoir si la source débitait pendant que le sender ne consommait rien. Absent pour
   * une source locale, où il n'y a pas de connexion entrante à interroger.
   */
  deviceId?: string | null,
): Promise<void> => {
  const deadline = Date.now() + ANGLE_MEDIA_TIMEOUT_MS;

  // Relevé d'ouverture du flux entrant, pour le comparer à celui de l'échec.
  const entrantAvant = deviceId ? await releverEntrant(deviceId) : null;

  let bytesSent = 0, framesEncoded = 0;
  let limitation = 'inconnue', targetBitrate = 0;
  let encodeur = 'absent', largeur = 0, hauteur = 0;

  while (Date.now() < deadline) {
    const stats = await pc.getStats();
    stats.forEach(report => {
      if (report.type === 'outbound-rtp' && report.kind === 'video') {
        bytesSent     = report.bytesSent     || 0;
        framesEncoded = report.framesEncoded || 0;
        limitation    = report.qualityLimitationReason ?? 'inconnue';
        targetBitrate = report.targetBitrate  || 0;
        // Absent tant que l'encodeur n'a pas été initialisé : c'est précisément ce
        // qu'on cherche à savoir.
        encodeur      = report.encoderImplementation || 'absent';
        largeur       = report.frameWidth  || 0;
        hauteur       = report.frameHeight || 0;
      }
    });

    if (bytesSent > 0 && framesEncoded > 0) return;

    await new Promise(resolve => setTimeout(resolve, ANGLE_MEDIA_POLL_MS));
  }

  const entrantApres = deviceId ? await releverEntrant(deviceId) : null;
  const definition   = largeur && hauteur ? `${largeur}×${hauteur}` : '—';

  throw new Error(
    `aucune image émise après ${ANGLE_MEDIA_TIMEOUT_MS / 1000}s `
    + `(piste ${track.readyState}, muted=${track.muted}, enabled=${track.enabled}, `
    + `label="${track.label || '—'}", entrée=${viaCanvas ? 'canvas' : 'piste directe'} ; `
    + `encodage : limitation=${limitation}, `
    + `débit visé=${Math.round(targetBitrate / 1000)} kbps, images=${framesEncoded}, `
    + `octets=${bytesSent}, encodeur=${encodeur}, définition=${definition}`
    + `${deviceId ? ` ; ${decrireEntrant(entrantAvant, entrantApres)}` : ''})`
  );
};

/**
 * Ferme la publication d'un angle. La session WHIP est explicitement supprimée côté
 * MediaMTX : sans ce DELETE, le path resterait « occupé » par un publisher fantôme et le
 * réenregistrement suivant du même angle échouerait.
 */
export const closeAnglePublication = async (publication: AnglePublication): Promise<void> => {
  // Avant la PeerConnection : la boucle de dessin et le <video> caché n'ont plus de
  // destinataire, et rien d'autre ne les arrêtera.
  try { publication.canvas?.dispose(); } catch { /* déjà libéré */ }
  try { publication.pc.close(); } catch { /* déjà fermée */ }
  await deleteWhipSession(publication.whipUrl);
};
